import { getIsLogged } from '@features/auth';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { capturePendingRedirectFromUrl, consumePendingRedirect, setPendingRedirect } from './pendingRedirect';

declare global {
    interface Window {
        /**
         * Point d'entree que le shell natif appelle via evaluateJavaScript quand
         * une notification est ouverte. Meme principe que window.setFcmToken :
         * depuis une WKWebView la communication est unidirectionnelle, le natif
         * ne peut que nous appeler.
         */
        setNotificationRoute?: (path: string) => void;
        /**
         * Destination posee par le shell natif AVANT que React n'ait monte.
         *
         * Au demarrage a froid, le natif injecte la route des la fin du chargement
         * de la page — instant auquel setNotificationRoute n'existe pas encore. Il
         * la depose donc aussi ici, et nous la relisons au montage.
         */
        __pendingNotificationRoute?: string | null;
    }
}

/**
 * Recoit la destination d'une notification et l'applique au bon moment.
 *
 * Deux cas se presentent, et ils n'appellent pas le meme traitement :
 *
 * - application deja ouverte et session active : on navigue immediatement ;
 * - demarrage a froid : l'utilisateur est deconnecte par useAutoDisconnect, la
 *   destination est donc mise en attente et consommee apres la connexion, dans
 *   useLoginPageVM.
 */
export const useNotificationRedirect = () => {
    const isLogged = useSelector(getIsLogged);
    const navigate = useNavigate();

    useEffect(() => {
        // Le shell natif peut aussi ouvrir directement /login?redirect=/news.
        capturePendingRedirectFromUrl();

        // Destination deposee par le natif avant notre montage.
        if (window.__pendingNotificationRoute) {
            setPendingRedirect(window.__pendingNotificationRoute);
            window.__pendingNotificationRoute = null;
        }
    }, []);

    useEffect(() => {
        window.setNotificationRoute = (path: string) => {
            setPendingRedirect(path);

            // Session active : la destination n'a pas a attendre un login.
            if (isLogged) {
                const target = consumePendingRedirect();

                if (target) {
                    navigate(target);
                }
            }
        };

        return () => {
            delete window.setNotificationRoute;
        };
    }, [isLogged, navigate]);
};
